/** Platform key → URL (e.g. { spotify: "...", bandcamp: "..." }) */
export type StreamLinks = Record<string, string>;

export type Track = {
  id: string;
  name: string;
  duration: number;
  position: number;
  streamUrl: string;
  account?: string;
  /** Bandcamp /track/{slug} for stream resolution; falls back to `id` when omitted. */
  bandcampId?: string;
  artist?: string;
  video?: string;
  isrc?: string;
  /** International Standard Musical Work Code (composition / work) */
  iswc?: string;
  bpm?: number;
  bpmTrusted?: boolean;
  camelotKey?: string;
  musicalKey?: string;
  energy?: number;
  genre?: string;
  /** When true, omitted from the live radio pool (sparse — store only if true). */
  excludeFromRadio?: boolean;
  /** Absolute or site-relative URL to timed lyrics (TTML). */
  lyricsUrl?: string;
  /**
   * Source release id (live radio queue). On-demand playback uses
   * `player.releaseId` instead.
   */
  releaseId?: string;
  /** Source album title when playing from live radio. */
  albumName?: string;
  /** Source release type for fetchRelease during live. */
  releaseType?: string;
  /** ReplayGain track gain in dB (optional). */
  replayGain?: number;
  replayGainTrack?: number;
  replayGainAlbum?: number;
  /** Track-local credits (writers, featured vocals, etc.). */
  credits?: { role: string; name: string }[];
};

export type Release = {
  id: string;
  name: string;
  type: string;
  label?: string;
  catalogNo?: string;
  upc?: string;
  artworkUrl: string;
  /** Optional square looping motion art on media B2 (`img/artwork/{id}.mp4|webm|gif`). */
  artworkMotionUrl?: string;
  releaseDate: string;
  description?: string;
  /** Prose/HTML string, or `{ role, name }[]`. */
  credits?: string | { role: string; name: string }[];
  tracks?: Track[];
  streamLinks?: StreamLinks;
  artist?: string;
};

export type MusicCatalog = {
  releases: Release[];
  compilations: Release[];
  remixes: Release[];
  collaborations: Release[];
};

export type PhotoMedia = {
  id: string;
  mediaUrl: string;
  mediaFormat: 'Photo' | 'Video' | string;
  posterUrl?: string;
  /** Place tagged on this slide (Instagram Graph `location`). */
  location?: string;
  /** Instagram explore/locations id for the tagged place. */
  locationId?: string;
  /** URL slug segment for the Instagram location page. */
  locationSlug?: string;
  /** When true, slide is omitted from the public site. */
  hidden?: boolean;
};

export type PhotoPost = {
  id: string;
  date: string;
  title: string;
  description?: string;
  format: string;
  /**
   * Post-level place from Instagram (parent media `location`).
   * Used as the Location meta fallback when the active slide has no place.
   */
  location?: string;
  /** Instagram explore/locations id for the post-level place. */
  locationId?: string;
  /** URL slug segment for the Instagram location page. */
  locationSlug?: string;
  /** Optional view count for the photo meta row. */
  views?: number;
  /** When true, entire post is omitted from the public site. */
  hidden?: boolean;
  media: PhotoMedia[];
};

export type SiteInfo = {
  bio: string;
  /**
   * IANA timezone for the live-radio footer clock.
   * DST follows the zone’s rules when the region observes it.
   */
  clockTimezone?: string;
  radioMood?: string;
  radioTrackOrder?: string[];
  /** Artist portrait on the www home panel — gif, image, or mp4 path/URL */
  portraitUrl?: string;
  portraitInvert?: boolean;
  portraitGrayscale?: boolean;
  portraitBrightness?: number;
  portraitContrast?: number;
  portraitSaturate?: number;
  portraitSepia?: number;
  portraitBlur?: number;
  portraitHueRotate?: number;
  portraitVignette?: boolean;
  portraitGradient?: string[];
  portraitGradientEnabled?: boolean;
  portraitBlendMode?: string;
  portraitGradientOpacity?: number;
  /** Visual zoom inside the frame (1–3). Does not crop the source file. */
  portraitZoom?: number;
  /** Horizontal focus 0–100%. */
  portraitFocusX?: number;
  /** Vertical focus 0–100%. */
  portraitFocusY?: number;
};

export type Database = {
  music: MusicCatalog;
  photo: PhotoPost[];
  info: SiteInfo;
};

export function formatTrackNumber(position: number): string {
  return String(position).padStart(2, '0');
}

export function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
